import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import UndoIcon from "@mui/icons-material/Undo";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import DefaultApiService from "../../../../../infrastructure/api/default";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import FileUploader from "../../../../components/shared/uploader/FileUploader";
import IconComponent from "../../../../components/shared/icon";

// Get base URL for file previews
const getBaseUrl = () => {
  return window.globalConfig?.API_URL || "";
};

const ProfileApprovalDetailPage = () => {
  const profileData = useLoaderData();
  const profile = profileData?.data || profileData;
  const navigate = useNavigate();
  const { userId } = useParams();
  const [loading, setLoading] = useState({ approve: false, reject: false });

  const onNavigateToListPage = () => {
    navigate(-1); // Go back to previous page
  };

  const onApprove = async () => {
    if (loading.approve) return;
    
    setLoading((prev) => ({ ...prev, approve: true }));
    try {
      const res = await DefaultApiService.postApi(
        "SLN/Management/ProfileApproval/ApproveProfile",
        { userId: parseInt(userId) }
      );
      if (res.isSuccess) {
        toast.success(res.message || "پروفایل با موفقیت تایید شد");
        navigate(-1); // Go back to list
      } else {
        toast.error(res.message || "خطا در تایید پروفایل");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");
    } finally {
      setLoading((prev) => ({ ...prev, approve: false }));
    }
  };

  const onReject = async () => {
    if (loading.reject) return;
    
    setLoading((prev) => ({ ...prev, reject: true }));
    try {
      const res = await DefaultApiService.postApi(
        "SLN/Management/ProfileApproval/RejectProfile",
        { userId: parseInt(userId), reason: "" }
      );
      if (res.isSuccess) {
        toast.success(res.message || "پروفایل رد شد");
        navigate(-1); // Go back to list
      } else {
        toast.error(res.message || "خطا در رد پروفایل");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");
    } finally {
      setLoading((prev) => ({ ...prev, reject: false }));
    }
  };

  const getGenderText = (gender) => {
    switch (gender) {
      case 1:
        return "مرد";
      case 2:
        return "زن";
      default:
        return "نامشخص";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 0:
        return "تکمیل نشده";
      case 1:
        return "تکمیل شده";
      case 2:
        return "تایید شده";
      default:
        return "نامشخص";
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Avatar>
                  <IconComponent name="user" width="16px" height="16px" />
                </Avatar>
                <Typography variant="body1" fontWeight={600}>
                  بررسی و تایید پروفایل
                </Typography>
              </Box>
            }
          />
          <CardContent sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              {/* اطلاعات کاربری */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  اطلاعات کاربری
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  label="نام کاربری"
                  value={profile?.userName || ""}
                  fullWidth
                  variant="outlined"
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  label="نام"
                  value={profile?.firstName || ""}
                  fullWidth
                  variant="outlined"
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  label="نام خانوادگی"
                  value={profile?.lastName || ""}
                  fullWidth
                  variant="outlined"
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  label="کد ملی"
                  value={profile?.nationalCode || ""}
                  fullWidth
                  variant="outlined"
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  label="شماره همراه"
                  value={profile?.mobile || ""}
                  fullWidth
                  variant="outlined"
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  label="ایمیل"
                  value={profile?.email || ""}
                  fullWidth
                  variant="outlined"
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  label="سن"
                  value={profile?.age || ""}
                  fullWidth
                  variant="outlined"
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  label="جنسیت"
                  value={getGenderText(profile?.gender)}
                  fullWidth
                  variant="outlined"
                  disabled
                />
              </Grid>

              {/* اطلاعات مشتری */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  اطلاعات مشتری
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  label="کد پستی"
                  value={profile?.postalCode || ""}
                  fullWidth
                  variant="outlined"
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  label="تلفن"
                  value={profile?.telephon || ""}
                  fullWidth
                  variant="outlined"
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={12}>
                <TextField
                  label="آدرس"
                  value={profile?.address || ""}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={12}>
                <TextField
                  label="توضیحات"
                  value={profile?.description || ""}
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  disabled
                />
              </Grid>

              {/* تصاویر کارت ملی */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  تصاویر کارت ملی
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <FileUploader
                  moduleName={"SLN/CustomerData"}
                  maxFiles={1}
                  label="تصویر کارت ملی (رو)"
                  baseUrl={getBaseUrl()}
                  placeholder={"تصویر کارت ملی را انتخاب کنید"}
                  accept="image/*"
                  value={profile?.nationalCardUrl}
                  disabled={true}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FileUploader
                  moduleName={"SLN/CustomerData"}
                  maxFiles={1}
                  label="تصویر کارت ملی (پشت)"
                  baseUrl={getBaseUrl()}
                  placeholder={"تصویر پشت کارت ملی را انتخاب کنید"}
                  accept="image/*"
                  value={profile?.nationalCardBackUrl}
                  disabled={true}
                />
              </Grid>

              {/* وضعیت */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  وضعیت
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <TextField
                  label="وضعیت تایید"
                  value={getStatusText(profile?.profileConfirmationStatus)}
                  fullWidth
                  variant="outlined"
                  disabled
                />
              </Grid>

              {/* دکمه‌های عملیات */}
              <Grid
                item
                xs={12}
                md={12}
                container
                justifyContent={"flex-end"}
                sx={{ mt: 3 }}
              >
                <Button
                  variant="outlined"
                  size="large"
                  color="gray"
                  sx={{ mx: 1 }}
                  startIcon={<UndoIcon />}
                  onClick={onNavigateToListPage}
                >
                  بازگشت
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  color="error"
                  sx={{ mx: 1 }}
                  startIcon={<CancelIcon />}
                  onClick={onReject}
                  disabled={loading.reject || loading.approve || profile?.profileConfirmationStatus === 0}
                >
                  {loading.reject && (
                    <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                  )}
                  رد پروفایل
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  color="success"
                  sx={{ mx: 1 }}
                  startIcon={<CheckCircleIcon />}
                  onClick={onApprove}
                  disabled={loading.approve || loading.reject || profile?.profileConfirmationStatus === 2}
                >
                  {loading.approve && (
                    <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                  )}
                  تایید پروفایل
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ProfileApprovalDetailPage;

